import { useMemo } from "react";
import { hashHueHex } from "@/lib/calendars";

import {
  BASE_SLOT_MINUTES,
  BASE_SLOT_REM,
  gridRowsForMinuteSpan,
} from "../calendar-time-axis";
import { isCancelledCalendarEvent } from "../calendar-utils";

// ── Session rail ────────────────────────────────────────────────────────────
// A thin, non-interactive background rail on the left edge of every day column
// showing *who is scheduled where* at a glance:
//   • Clinic view (groupBy "tenant"): each column is one clinic → the rail shows
//     which DOCTOR is running a session there, and for what span.
//   • Doctor view (groupBy "doctor"): each column is one doctor → the rail shows
//     which CLINIC that doctor sits in.
//
// A "session" is derived purely from the events the column already has: the
// contiguous time span for which a given (doctor, clinic) pair has slot OR
// appointment rows on that day. Each band also shows how many patients are booked
// into that session (appointments, excluding cancelled). Where two+ entities overlap in time the rail is
// split vertically into N equal bands for that sub-span so both names stay
// legible (see the reference prototype "Calendar Session Rail v2").

const RAIL_LABEL_FONT_PX = 6.4; // ~px per char at 10px/700 Lato, vertical
const RAIL_LABEL_PAD_PX = 14; // padding + rounding fudge in the length estimate
const RAIL_BAND_GAP_PX = 4; // 2px top + 2px bottom margin per band
const RAIL_BAND_PAD_PX = 12; // 6px top + 6px bottom padding per band
const ROOT_FONT_PX = 16; // rem→px base (Tailwind/browser default)
const RAIL_COUNT_PX = 14; // vertical room the patient-count badge takes off a band
const RAIL_COUNT_MIN_BAND_PX = 30; // below this a band only has room for the name

// ── Color helpers (mirror the prototype's hex math exactly) ──────────────────
const SLATE_FALLBACK = { r: 100, g: 116, b: 139 }; // #64748B

function hexToRgb(hex) {
  const raw = String(hex || "").replace("#", "");
  const normalized = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return { ...SLATE_FALLBACK };
  const num = Number.parseInt(normalized, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function clamp255(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map((v) => clamp255(v).toString(16).padStart(2, "0")).join("")}`;
}

function mixHex(hexA, hexB, weight) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  return rgbToHex({
    r: a.r * (1 - weight) + b.r * weight,
    g: a.g * (1 - weight) + b.g * weight,
    b: a.b * (1 - weight) + b.b * weight,
  });
}

function rgbaHex(hex, alpha) {
  const c = hexToRgb(hex);
  return `rgba(${c.r},${c.g},${c.b},${alpha})`;
}

// hashHueHex (the deterministic hue for entities with no calendar colour) now lives in
// src/lib/calendars.js so standalone calendars share the exact same palette.

function normalizeHex(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const normalized = raw.startsWith("#") ? raw.slice(1) : raw;
  if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
    return `#${normalized.split("").map((c) => c + c).join("").toUpperCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return `#${normalized.toUpperCase()}`;
  }
  return "";
}

// Muted band palette. Returns BOTH the light and dark values as CSS custom
// properties so the band switches with the app's `.dark` class (the same
// dual-var pattern CalendarEventItem uses) — no JS theme flag needed.
function bandThemeVars(hex, colorMode) {
  if (colorMode === "neutral") {
    return {
      "--rail-bg-light": "oklch(0.93 0 0)",
      "--rail-bd-light": "oklch(0.65 0 0)",
      "--rail-tx-light": "oklch(0.32 0 0)",
      "--rail-bg-dark": "oklch(0.30 0.02 264 / 70%)",
      "--rail-bd-dark": "oklch(0.55 0.02 264)",
      "--rail-tx-dark": "oklch(0.78 0.01 264)",
    };
  }
  return {
    "--rail-bg-light": rgbaHex(hex, 0.16),
    "--rail-bd-light": rgbaHex(hex, 0.6),
    "--rail-tx-light": mixHex(hex, "#000000", 0.32),
    "--rail-bg-dark": rgbaHex(hex, 0.22),
    "--rail-bd-dark": rgbaHex(hex, 0.7),
    "--rail-tx-dark": mixHex(hex, "#ffffff", 0.6),
  };
}

// ── Label helpers ────────────────────────────────────────────────────────────
// Prefer the full name; fall back to the short code when the band is too short to
// hold it vertically (est. ~6.4px per char at 10px Lato + padding).
function railLabelFor(name, short, availablePx) {
  const need = String(name || "").length * RAIL_LABEL_FONT_PX + RAIL_LABEL_PAD_PX;
  if (need <= availablePx) return name;
  return short || name;
}

// Does this event put a patient in the chair? Slots are availability, not
// bookings, and cancelled appointments do not count — note the calendar keeps
// cancelled events in the array when "Show cancelled" is on, so this guard is
// load-bearing rather than defensive.
function isBookedAppointment(event) {
  return event?.kind === "appointment" && !isCancelledCalendarEvent(event);
}

// Doctor initials: "Audrey Pang" → "AP", "Jayant V Iyer" → "JVI".
function doctorInitials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  return parts.map((p) => p[0].toUpperCase()).join("").slice(0, 4);
}

// Clinic short code when the tenant has no explicit shortName: an acronym for
// multi-word names ("Healo Technical" → "HT") or an uppercase truncation for a
// single word ("Clementi" → "CLEME").
function clinicShortCode(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length > 1) {
    return parts.map((p) => p[0].toUpperCase()).join("").slice(0, 5);
  }
  return parts[0].slice(0, 5).toUpperCase();
}

function minutesOf(dateTime) {
  return dateTime.hour * 60 + dateTime.minute;
}

// The "other" entity a column event contributes a band for: the clinic in doctor
// view, the doctor in clinic view. Returns null when the event lacks that id.
function otherEntityForEvent(event, groupBy) {
  if (groupBy === "doctor") {
    const id = String(event?.tenantId || "").trim();
    if (!id) return null;
    const tenant = event?._tenant || null;
    const name =
      String(tenant?.name || "").trim() ||
      String(tenant?.shortName || "").trim() ||
      String(event?.location || "").trim() ||
      id;
    const short = String(tenant?.shortName || "").trim() || clinicShortCode(name);
    // Clinics carry no calendar color of their own (colors are keyed by
    // calendar/doctor), so give each a stable hashed hue instead.
    return { id, name, short, colorHex: "" };
  }
  const id = String(event?.doctorId || "").trim();
  if (!id) return null;
  const name = String(event?.doctor || "").trim() || id;
  const short = doctorInitials(name) || name.slice(0, 3).toUpperCase();
  return { id, name, short, colorHex: normalizeHex(event?.colorHex) };
}

// Collapse each entity's sessions into contiguous spans (adjacent/overlapping
// spans merge into one), summing each span's booked-appointment count as it grows.
// The `{ ...session }` spread carries `count` onto a span that starts fresh.
function mergeSessions(sessions) {
  const byEntity = new Map();
  for (const session of sessions) {
    if (!byEntity.has(session.entityId)) byEntity.set(session.entityId, []);
    byEntity.get(session.entityId).push(session);
  }
  const merged = [];
  for (const list of byEntity.values()) {
    list.sort((a, b) => a.start - b.start);
    let current = null;
    for (const session of list) {
      if (current && session.start <= current.end) {
        current.end = Math.max(current.end, session.end);
        current.count += session.count;
      } else {
        if (current) merged.push(current);
        current = { ...session };
      }
    }
    if (current) merged.push(current);
  }
  return merged;
}

// Sweep the distinct start/end points; each sub-span collects the entities active
// across it. bands.length > 1 means "stack".
function buildRailSegments(merged) {
  if (!merged.length) return [];
  const points = new Set();
  for (const span of merged) {
    points.add(span.start);
    points.add(span.end);
  }
  const sorted = [...points].sort((a, b) => a - b);
  const segments = [];
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const start = sorted[i];
    const end = sorted[i + 1];
    if (end <= start) continue;
    const active = merged.filter((span) => span.start <= start && span.end >= end);
    if (!active.length) continue;
    segments.push({ start, end, bands: active });
  }
  return segments;
}

// Approximate px-per-minute of the rendered grid, taken from the axis tick
// geometry (which already folds in the zoom) so the short-code fallback estimate
// tracks the actual band height.
function railPxPerMinute(axis) {
  const tick = axis?.ticks?.[0];
  const remPerMinute =
    tick && tick.minutes ? tick.heightRem / tick.minutes : BASE_SLOT_REM / BASE_SLOT_MINUTES;
  return remPerMinute * ROOT_FONT_PX;
}

/**
 * Build the positioned rail segments for one column's events. Pure derivation —
 * no fetch, no new state.
 *
 * @param {Array} events   the column's events (slots + appointments)
 * @param {"tenant"|"doctor"} groupBy
 * @param {ReturnType<typeof import("../calendar-time-axis").getCalendarTimeAxis>} axis
 * @param {{ colorMode?: "accent"|"neutral" }} [options]
 * @returns {Array<{ key: string, startRow: number, span: number, bands: Array }>}
 *          Each band carries `patientCount` — the booked, non-cancelled
 *          appointments across the WHOLE session the band belongs to.
 */
export function buildSessionRailSegments(events, groupBy, axis, options = {}) {
  if (!Array.isArray(events) || events.length === 0 || !axis) return [];
  const colorMode = options.colorMode === "neutral" ? "neutral" : "accent";

  // 1. Sessions grouped by the "other" entity, plus per-entity metadata + a vote
  //    for the representative calendar color (doctors) — clinics fall through to a
  //    stable hashed hue.
  const sessions = [];
  const meta = new Map();
  for (const event of events) {
    if (!event?.startAt || !event?.endAt) continue;
    const entity = otherEntityForEvent(event, groupBy);
    if (!entity) continue;
    const start = minutesOf(event.startAt);
    let end = minutesOf(event.endAt);
    if (end <= start) end = 24 * 60; // guard midnight-as-0 ends
    sessions.push({ entityId: entity.id, start, end, count: isBookedAppointment(event) ? 1 : 0 });
    if (!meta.has(entity.id)) {
      meta.set(entity.id, { name: entity.name, short: entity.short, colorVotes: new Map() });
    }
    if (entity.colorHex) {
      const votes = meta.get(entity.id).colorVotes;
      votes.set(entity.colorHex, (votes.get(entity.colorHex) || 0) + 1);
    }
  }
  if (!sessions.length) return [];

  // 2. Resolve each entity's accent color: the most common event color when the
  //    events carry one specific to the entity (doctors), else a hashed hue.
  const colorByEntity = new Map();
  for (const [id, info] of meta) {
    let best = "";
    let bestCount = 0;
    for (const [hex, count] of info.colorVotes) {
      if (count > bestCount) {
        best = hex;
        bestCount = count;
      }
    }
    colorByEntity.set(id, best || hashHueHex(id));
  }

  // 3. Merge → segments → positioned bands.
  const merged = mergeSessions(sessions);
  const rawSegments = buildRailSegments(merged);
  const pxPerMinute = railPxPerMinute(axis);

  const segments = [];
  for (const segment of rawSegments) {
    const rows = gridRowsForMinuteSpan(segment.start, segment.end, axis);
    if (!rows) continue; // fully outside the visible window
    const bandCount = segment.bands.length;
    const bandInnerPx =
      ((segment.end - segment.start) * pxPerMinute) / bandCount -
      RAIL_BAND_GAP_PX -
      RAIL_BAND_PAD_PX;
    segments.push({
      key: `${segment.start}-${segment.end}`,
      startRow: rows.startRow,
      span: rows.span,
      bands: segment.bands.map((band) => {
        const info = meta.get(band.entityId);
        const name = info?.name || band.entityId;
        const short = info?.short || name;
        // `band` IS the merged span, so this is the count for the WHOLE session —
        // the same number on every sub-segment it was sliced into. Do not
        // recompute it per sub-span, or one session would show a number that
        // changes partway down the rail.
        const patientCount = band.count || 0;
        const showCount = patientCount > 0 && bandInnerPx >= RAIL_COUNT_MIN_BAND_PX;
        return {
          key: band.entityId,
          name,
          patientCount,
          showCount,
          label: railLabelFor(name, short, showCount ? bandInnerPx - RAIL_COUNT_PX : bandInnerPx),
          vars: bandThemeVars(colorByEntity.get(band.entityId), colorMode),
        };
      }),
    });
  }
  return segments;
}

/**
 * Non-interactive background rail layer. Renders as a grid sibling of the events
 * `<ol>` (same `fineRowTemplate`) so its bands land on exactly the same rows as
 * the cards. Sits behind the events (the events container is inset by the rail
 * width so cards never overlap it) and never intercepts pointer events.
 */
export function SessionRail({
  events,
  groupBy,
  timeAxis,
  railWidthPx = 26,
  railMarginPx = 0,
  colorMode = "accent",
}) {
  const segments = useMemo(
    () => buildSessionRailSegments(events, groupBy, timeAxis, { colorMode }),
    [events, groupBy, timeAxis, colorMode],
  );

  if (!segments.length) return null;

  return (
    <div
      aria-hidden
      style={{ gridTemplateRows: timeAxis.fineRowTemplate }}
      className="pointer-events-none col-start-1 col-end-2 row-start-1 grid grid-cols-1"
    >
      {segments.map((segment) => (
        // The segment occupies its grid rows for placement, but its bands live in
        // an ABSOLUTELY-positioned child so the rail contributes zero intrinsic
        // height (exactly like the event cards below). Otherwise a tall vertical
        // label would set a content min-height, pushing the column taller than the
        // hour-labels gutter once the rows shrink on zoom-out — misaligning cards.
        <div
          key={segment.key}
          style={{ gridRow: `${segment.startRow} / span ${segment.span}` }}
          className="relative col-start-1 min-h-0"
        >
          <div
            style={{ width: `${railWidthPx}px`, left: `${railMarginPx}px` }}
            className="absolute inset-y-0 flex flex-col overflow-hidden"
          >
            {segment.bands.map((band) => (
              <div
                key={band.key}
                title={
                  band.patientCount > 0
                    ? `${band.name} — ${band.patientCount} patient${band.patientCount === 1 ? "" : "s"} this session`
                    : band.name
                }
                style={{
                  ...band.vars,
                  flex: "1 1 0",
                  margin: "2px 0",
                  // The extra bottom padding reserves the strip the count badge is
                  // absolutely positioned into, so `max-h-full` stops the vertical
                  // name before it runs under the number.
                  padding: band.showCount ? "6px 0 20px" : "6px 0",
                }}
                className="relative flex min-h-0 items-center justify-center overflow-hidden rounded-r-[4px] bg-(--rail-bg-light) dark:bg-(--rail-bg-dark)"
              >
                {/* 3px left accent, drawn as a bar (not a border) so it dodges the
                    app-wide `* { @apply border-border }` that clobbers border-color. */}
                <span
                  aria-hidden
                  className="absolute inset-y-0 left-0 w-[3px] bg-(--rail-bd-light) dark:bg-(--rail-bd-dark)"
                />
                <span className="max-h-full rotate-180 overflow-hidden whitespace-nowrap text-[10px] font-bold leading-none tracking-[0.02em] text-(--rail-tx-light) dark:text-(--rail-tx-dark) [text-orientation:mixed] [writing-mode:vertical-rl]">
                  {band.label}
                </span>
                {/* Patient count. Horizontal (not rotated) so it stays legible at a
                    glance, and absolutely placed so it adds no intrinsic height —
                    the whole rail must stay height-neutral or the column outgrows
                    the hour gutter on zoom-out. */}
                {band.showCount ? (
                  <span className="absolute inset-x-0 bottom-[3px] text-center font-mono text-[9px] font-semibold leading-none tabular-nums text-(--rail-tx-light) dark:text-(--rail-tx-dark)">
                    {band.patientCount}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
